'use no memo';

import { type DebouncedFunc, debounce } from 'lodash-es';
import { type DependencyList, useCallback, useEffect, useRef } from 'react';

/**
 * デバウンスのデフォルト遅延時間。
 * テキスト入力などのイベントハンドラに使用する。
 */
const DEFAULT_INPUT_DEBOUNCE = 250;

/**
 * デバウンスされたコールバックを返す。テキスト入力などのイベントハンドラに使用する。
 * beautiful-react-hooksの`useDebouncedCallback`を参考に作成。
 * @see https://github.com/antonioru/beautiful-react-hooks/blob/master/src/useDebouncedCallback.ts
 *
 * @param callback 対象のコールバック
 * @param deps コールバックの依存リスト。useCallbackやuseMemoのdepsと同じ
 * @param delay デバウンスの遅延時間。デフォルトは{@link DEFAULT_INPUT_DEBOUNCE}
 * @returns デバウンスされたコールバック
 */

// biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
export function useDebouncedCallback<T extends (...args: any[]) => any>(callback: T, deps: DependencyList, delay: number = DEFAULT_INPUT_DEBOUNCE): DebouncedFunc<T> {
  const debounced = useRef(debounce(callback, delay));
  useEffect(() => {
    debounced.current = debounce(callback, delay);
    return () => debounced.current.cancel();
  }, [callback, delay]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps, react-hooks-js/use-memo, react-hooks-js/refs -- deps はユーザー定義。debounced.current は ref 経由で意図的
  return useCallback(debounced.current, deps) as DebouncedFunc<T>;
}